import { BaseService } from '@services/BaseService';
import { OptionsViewModel, PageViewModel } from '@models/common.models';
import { InvoiceViewModel } from '@models/Invoice';
import {
  InvoicePaymentItemViewModel,
  InvoiceLineCreateViewModel,
  InvoicePaymentHistoryViewModel
} from '@models/InvoicePaymentItem';
import { InvoiceSearchViewModel } from '../_models/Invoice';
import { InvoicePayeesViewModel } from '@models/InvoicePayees';
import { InvoiceCreateViewModel } from '@models/Invoicing';

class InvoicingService extends BaseService {
  constructor() {
    super('invoicing');
  }

  getInvoice(invoiceId: number) {
    return this.fetch<InvoiceViewModel>(`invoices/${invoiceId}`, {
      method: 'GET'
    });
  }

  getInvoicePayees(swoId: number) {
    return this.fetch<InvoicePayeesViewModel>(`invoices/${swoId}/payees`, {
      method: 'GET'
    });
  }

  createInvoiceHeader(invoice: InvoiceCreateViewModel) {
    return this.fetch<InvoiceViewModel>(`invoices`, {
      method: 'POST',
      body: JSON.stringify(invoice)
    });
  }

  updateInvoiceHeader(id: number, invoice: InvoiceCreateViewModel) {
    return this.fetch<InvoiceViewModel>(`invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(invoice)
    });
  }

  getGLCodes(payeeType?: number) {
    const queryString = payeeType !== undefined ? '?payeeType=' + payeeType : '';
    return this.fetch<OptionsViewModel>(`gpglcodes${queryString}`, {
      method: 'GET'
    });
  }

  createLineItem(invoiceId: number, line: InvoiceLineCreateViewModel) {
    return this.fetch(`invoices/${invoiceId}/line`, {
      method: 'POST',
      body: JSON.stringify(line)
    });
  }

  generateAllInvoiceLinesForSwo(invoiceId: number) {
    return this.fetch(`invoices/${invoiceId}/lines`, {
      method: 'POST',
      body: ''
    });
  }

  updateLineItem(invoiceId: number, lineNumber: number, line: InvoiceLineCreateViewModel) {
    return this.fetch(`invoices/${invoiceId}/line/${lineNumber}`, {
      method: 'PUT',
      body: JSON.stringify(line)
    });
  }

  getLineTypes() {
    return this.fetch<OptionsViewModel>(`linetypes`, {
      method: 'GET'
    });
  }

  getInvoicePaymentFilters() {
    return this.fetch<Array<OptionsViewModel>>('filters', {
      method: 'GET'
    });
  }

  getInvoicePaymentItems(invoiceId: string): Promise<Array<InvoicePaymentItemViewModel>> {
    return this.fetch<Array<InvoicePaymentItemViewModel>>(`invoices/${invoiceId}/paymentitems`, {
      method: 'GET'
    });
  }

  duplicateInvoiceCode(vendorId: number, invoiceCode: string) {
    return this.fetch(`invoices/duplicate?vendorId=${vendorId}&invoiceCode=${invoiceCode}`, {
      method: 'GET'
    });
  }

  deleteInvoicePaymentItem(invoiceId: string, lineNumber: number) {
    return this.fetch(`invoices/${invoiceId}/paymentitems/${lineNumber}`, {
      method: 'DELETE'
    });
  }

  ownershipChange(id: number, release?: boolean) {
    if (release) {
      return this.fetch(`invoices/${id}/release`, {
        method: 'POST'
      });
    } else {
      return this.fetch(`invoices/${id}/own`, {
        method: 'POST'
      });
    }
  }

  searchInvoiceList(
    query: string | null,
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    orderDirection: string,
    filterBy: string | null,
    swoDispatchType?: string
  ) {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;
    let body = JSON.stringify({
      query: query,
      pageNumber: pn,
      pageSize: ps,
      orderBy: orderBy,
      orderDirection: orderDirection,
      filterBy: filterBy
    });
    let uri: string = 'invoices/search';
    if (swoDispatchType) {
      uri = uri.concat(`?swoDispatchType=${encodeURIComponent(swoDispatchType)}`);
    }
    return this.fetch<PageViewModel<InvoiceSearchViewModel>>(uri, {
      method: 'POST',
      body: body
    });
  }

  getStatuses() {
    return this.fetch<OptionsViewModel>(`statuses`, {
      method: 'GET'
    });
  }

  getInvoicePaymentHistory(invoiceId: number): Promise<Array<InvoicePaymentHistoryViewModel>> {
    return this.fetch<Array<InvoicePaymentHistoryViewModel>>(`invoices/${invoiceId}/paymentHistory`, {
      method: 'GET'
    });
  }
}

export const invoiceService = new InvoicingService();



// WEBPACK FOOTER //
// ./src/_services/InvoiceService.ts